import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Loader2, Activity, RefreshCw, AlertTriangle, Clock,
} from 'lucide-react';
import {
  Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList,
} from 'recharts';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';


function fmtInt(n) {
  return typeof n === 'number' ? n.toLocaleString() : '-';
}

function fmtResetTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const diffMs = d.getTime() - Date.now();
  if (diffMs <= 0) return `~${d.toLocaleTimeString()} (may have already reset)`;
  const hours = Math.floor(diffMs / 3600000);
  const mins = Math.round((diffMs % 3600000) / 60000);
  const rel = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  return `~${d.toLocaleTimeString()} (in ${rel})`;
}

/** Compact live quota readout: day-calls remaining vs limit, reserve
 * floor, reset time. Shared by the Diagnostics panel (full) and the
 * Integrations Xero card (mini). */
export function XeroQuotaStateRow({ state }) {
  const reserved = state.quota_reserved;
  return (
    <div
      key={state.tenant_id}
      className={`flex items-center justify-between gap-2 text-xs font-mono rounded px-2 py-1 ${
        reserved ? 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400' : ''
      }`}
    >
      <span className="truncate max-w-[160px]">{state.tenant_id}</span>
      <span className="flex items-center gap-2">
        <span className={reserved ? 'font-semibold' : ''}>
          {fmtInt(state.day_remaining)} / {fmtInt(state.day_limit)} remaining
        </span>
        {reserved && (
          <Badge variant="outline" className="text-[9px] h-4 border-amber-500 text-amber-700 dark:text-amber-400">
            quota_reserved (floor {fmtInt(state.reserve_floor)})
          </Badge>
        )}
        {state.estimated_reset_at && (
          <span className="text-muted-foreground">resets {fmtResetTime(state.estimated_reset_at)}</span>
        )}
      </span>
    </div>
  );
}


export function XeroQuotaPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [probing, setProbing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await diagnosticsAPI.xeroQuota();
      setData(d);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleProbe = async () => {
    setProbing(true);
    try {
      const { data: result } = await diagnosticsAPI.xeroQuotaProbe();
      if (result.success) {
        toast.success('Probe complete. Refreshing data.');
      } else {
        toast.error(result.error || 'Probe failed');
      }
      // Refresh the card data to show the new call + headers.
      await fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setProbing(false);
    }
  };

  const isEmpty = data && data.total_calls_24h === 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="h-5 w-5 text-indigo-600" />
          Xero Quota
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            disabled={loading}
            onClick={fetchData}
          >
            {loading
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <RefreshCw className="h-3 w-3" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading call log...
          </div>
        )}

        {isEmpty && (
          <div className="border rounded-lg p-4 text-sm text-muted-foreground">
            No calls recorded since instrumentation deployed.
            Data will appear here once Xero API calls are made through
            the instrumented code path.
          </div>
        )}

        {data && !isEmpty && (
          <>
            {/* Summary row */}
            <div className="flex items-center gap-4 text-sm">
              <span className="font-medium">
                {fmtInt(data.total_calls_24h)} calls in last 24h
              </span>
              {data.latest_problem && (
                <Badge variant="destructive" className="text-xs">
                  Rate limit: {data.latest_problem.problem}
                </Badge>
              )}
            </div>

            {/* Live quota state: remaining vs limit, reserve floor, reset time */}
            {data.quota_state && data.quota_state.length > 0 && (
              <div className="border rounded-lg p-2 space-y-1">
                <p className="text-xs font-medium">Live quota (last observed per tenant)</p>
                {data.quota_state.map((s) => (
                  <XeroQuotaStateRow key={s.tenant_id} state={s} />
                ))}
              </div>
            )}

            {/* Per-tenant totals vs limit */}
            {data.per_tenant_totals && data.per_tenant_totals.length > 0 && (
              <div className="border rounded-lg p-2 space-y-1">
                <p className="text-xs font-medium">Calls per tenant (24h vs 5,000/day)</p>
                {data.per_tenant_totals.map((t) => {
                  const pct = (t.count / t.day_limit * 100).toFixed(1);
                  const isHigh = t.count > 4000;
                  return (
                    <div key={t.tenant_id} className="flex items-center justify-between text-[10px] font-mono">
                      <span className="truncate max-w-[200px]">{t.tenant_id}</span>
                      <span className={isHigh ? 'text-red-600 font-medium' : ''}>
                        {fmtInt(t.count)} / {fmtInt(t.day_limit)} ({pct}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* By-caller table */}
            {data.by_caller && data.by_caller.length > 0 && (
              <div className="border rounded-lg p-2 space-y-1">
                <p className="text-xs font-medium">Calls by caller (24h)</p>
                <div className="grid grid-cols-[1fr_60px_120px] gap-x-2 text-[10px] font-mono text-muted-foreground border-b pb-1">
                  <span>Caller</span>
                  <span className="text-right">Count</span>
                  <span className="text-right">Last call</span>
                </div>
                {data.by_caller.map((c) => (
                  <div key={c.caller} className="grid grid-cols-[1fr_60px_120px] gap-x-2 text-[10px] font-mono">
                    <span className="truncate">{c.caller}</span>
                    <span className="text-right">{fmtInt(c.count)}</span>
                    <span className="text-right text-muted-foreground">
                      {c.last_call ? new Date(c.last_call).toLocaleTimeString() : '-'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Hourly bar chart */}
            {data.hourly && data.hourly.length > 0 && (
              <div className="border rounded-lg p-2 space-y-1">
                <p className="text-xs font-medium">Calls per hour (24h)</p>
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart
                    data={data.hourly}
                    margin={{ top: 16, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis
                      dataKey="hour"
                      tick={{ fontSize: 8 }}
                      tickFormatter={(v) => {
                        const d = new Date(v);
                        return `${d.getHours().toString().padStart(2, '0')}:00`;
                      }}
                    />
                    <YAxis tick={{ fontSize: 8 }} width={30} />
                    <Tooltip
                      labelFormatter={(v) => new Date(v).toLocaleString()}
                      formatter={(v) => [v, 'calls']}
                    />
                    <Bar dataKey="count" fill="#6366f1" radius={[2, 2, 0, 0]}>
                      <LabelList dataKey="count" position="top" fontSize={8} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Latest rate-limit headers per tenant */}
            {data.latest_headers && data.latest_headers.length > 0 && (
              <div className="border rounded-lg p-2 space-y-1">
                <p className="text-xs font-medium">Latest rate-limit headers (as of last call)</p>
                {data.latest_headers.map((h) => (
                  <div key={h.tenant_id} className="text-[10px] font-mono space-y-0.5 border-b pb-1 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate max-w-[200px]">{h.tenant_id}</span>
                      <span className="text-muted-foreground">
                        via {h.caller} at {h.captured_at ? new Date(h.captured_at).toLocaleString() : '-'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 pl-2 text-muted-foreground">
                      {Object.keys(h.rate_headers).length === 0 ? (
                        <span className="text-amber-600">No rate-limit headers present in response</span>
                      ) : (
                        Object.entries(h.rate_headers).map(([k, v]) => (
                          <span key={k}>
                            {k}: <span className={
                              k === 'x_rate_limit_problem' ? 'text-red-600 font-medium' :
                              k === 'x_day_limit_remaining' && v < 100 ? 'text-amber-600 font-medium' :
                              ''
                            }>{v}</span>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Latest X-Rate-Limit-Problem */}
            {data.latest_problem && (
              <div className="border-2 border-red-300 dark:border-red-700 rounded-lg p-2 bg-red-50 dark:bg-red-950/20">
                <div className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="font-medium text-red-700 dark:text-red-400">
                    Rate limit problem: {data.latest_problem.problem}
                  </span>
                </div>
                <div className="text-[10px] font-mono text-red-600/80 mt-1 pl-6">
                  Seen on {data.latest_problem.tenant_id} via {data.latest_problem.caller}
                  {data.latest_problem.captured_at && (
                    <> at {new Date(data.latest_problem.captured_at).toLocaleString()}</>
                  )}
                </div>
              </div>
            )}

            {/* Last 10 429s */}
            {data.last_429s && data.last_429s.length > 0 && (
              <div className="border rounded-lg p-2 space-y-1">
                <p className="text-xs font-medium flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3 text-amber-600" />
                  Recent 429 errors
                </p>
                {data.last_429s.map((err, i) => (
                  <div key={i} className="text-[10px] font-mono border-b pb-1 last:border-0 space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Clock className="h-2.5 w-2.5 text-muted-foreground flex-shrink-0" />
                      <span>{err.timestamp ? new Date(err.timestamp).toLocaleString() : '-'}</span>
                      <Badge variant="outline" className="text-[8px] h-4">{err.caller}</Badge>
                      <span className="text-muted-foreground">{err.tenant_id}</span>
                    </div>
                    <div className="pl-4 text-muted-foreground">
                      {err.rate_limit_problem && <span className="text-red-600">limit={err.rate_limit_problem} </span>}
                      {err.retry_after != null && <span>retry_after={err.retry_after}s </span>}
                      {err.body_snippet && (
                        <span className="block truncate max-w-full">{err.body_snippet}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Probe button (always visible) */}
        <div className="border-t pt-2">
          <Button
            variant="outline" size="sm"
            disabled={probing}
            onClick={handleProbe}
            className="text-xs"
          >
            {probing
              ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
              : <Activity className="h-3 w-3 mr-1" />}
            Probe quota now (costs 1 call)
          </Button>
          <p className="text-[10px] text-muted-foreground mt-1">
            Makes one GET /connections call to capture current rate-limit headers.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/** Compact single-line quota readout for the Integrations Xero card
 * (CLAUDE.md Prompt 26 FIX 3). Same data source as XeroQuotaPanel
 * (GET /admin/diagnostics/xero-quota), zero additional Xero calls. */
export function XeroQuotaMiniBadge() {
  const [data, setData] = useState(null);

  useEffect(() => {
    diagnosticsAPI.xeroQuota()
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null));
  }, []);

  if (!data || !data.quota_state || data.quota_state.length === 0) return null;

  const topConsumer = data.by_caller && data.by_caller[0];
  const worst = data.quota_state.reduce((min, s) => (
    (s.day_remaining ?? Infinity) < (min?.day_remaining ?? Infinity) ? s : min
  ), null);
  if (!worst || worst.day_remaining == null) return null;

  return (
    <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
      <Activity className="h-3.5 w-3.5 text-indigo-600" />
      <span className={worst.quota_reserved ? 'text-amber-600 font-semibold' : ''}>
        Day quota: {fmtInt(worst.day_remaining)} / {fmtInt(worst.day_limit)} remaining
      </span>
      {worst.estimated_reset_at && (
        <span>(resets {fmtResetTime(worst.estimated_reset_at)})</span>
      )}
      {topConsumer && (
        <span>· top consumer: {topConsumer.caller} ({fmtInt(topConsumer.count)})</span>
      )}
    </div>
  );
}
